import { app } from "../model/AppModel";
import type ConfigurationCollection from "../model/ConfigurationCollection";

export type CriticalConfigurationEntry = {
  name: string;
  value: string;
};

export function normalizeCriticalConfigurations(
  configuration: CriticalConfigurationEntry[] = []
): CriticalConfigurationEntry[] {
  return configuration
    .map((entry) => ({
      name: entry?.name ?? "",
      value: entry?.value ?? "",
    }))
    .filter((entry) => entry.name);
}

/**
 * Fusionne les configs critiques Gatsby dans une collection (upsert par `name`).
 * Ne remplace pas la collection setupweb — évite `configuration: undefined` sur `app`.
 */
export async function mergeCriticalConfigurationsIntoCollection(
  criticalEntries: CriticalConfigurationEntry[] = [],
  targetCollection?: ConfigurationCollection | null
): Promise<ConfigurationCollection | undefined> {
  const normalized = normalizeCriticalConfigurations(criticalEntries);

  const { default: ConfigurationCollectionClass } = await import(
    /* webpackChunkName: "backbone-configuration" */ "../model/ConfigurationCollection"
  );

  let collection =
    targetCollection ??
    (app.get("configuration") as ConfigurationCollection | undefined);

  if (!collection) {
    if (!normalized.length) {
      return undefined;
    }
    collection = new ConfigurationCollectionClass([]);
  }

  if (!normalized.length) {
    return collection;
  }

  normalized.forEach(({ name, value }) => {
    const existing = collection!.findWhere({ name });
    if (existing) {
      existing.set({ name, value });
    } else {
      collection!.add({ name, value });
    }
  });

  return collection;
}

/**
 * Injecte les configurations critiques Gatsby dans `app` (léger, sans setupweb).
 * Permet à `app.c()` de fonctionner avant le SetupModel post-LCP.
 */
export async function bootstrapBackboneCriticalConfiguration(
  configuration: CriticalConfigurationEntry[] = []
): Promise<ConfigurationCollection | undefined> {
  const collection = await mergeCriticalConfigurationsIntoCollection(
    configuration
  );
  if (collection) {
    app.set("configuration", collection);
  }
  return collection;
}
